import gql from "graphql-tag";
import type * as React from "react";

import {RenderState, useRenderState} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useFastlyQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {useRegionOverride} from "@khan/data-region-override";
import {PermissionsInfo} from "@khan/shared-core";

import type {DomainObject} from "../types.ts";

import type {
    learnMenuTopicsQuery,
    learnMenuTopicsQueryVariables,
} from "./__graphql-types__/learnMenuTopicsQuery.ts";

export const DATA_QUERY: GqlOperation<
    learnMenuTopicsQuery,
    learnMenuTopicsQueryVariables
> = gqlOp(gql`
    query learnMenuTopicsQuery($region: String) {
        learnMenuTopics(region: $region) {
            domainId
            slug
            translatedTitle
            href
            isNew
            icon
            children {
                courseId
                slug
                translatedTitle
                href
                isNew
                loggedOutHref
                nonContentLink
            }
        }
    }
`);

export type MappedResult = {
    loading: boolean;
    error: Error | null | undefined;
    domains: Array<DomainObject>;
};

type OwnProps = {
    /**
     * When true, the query is rendered during SSR; otherwise it is not.
     * Defaults to false.
     */
    ssr?: boolean;
    /**
     * A function that returns children to render with a given query result.
     */
    children: (result: MappedResult) => React.ReactNode;
};

type Props = OwnProps;

/**
 * A query component in charge of getting the list of domains and topics used in
 * the course menu on the global header and on the logged-out homepage.
 */
const LearnMenuTopicsQuery = ({ssr, children}: Props): React.ReactElement => {
    const renderState = useRenderState();
    const regionResult = useRegionOverride(ssr);

    // Unless it's the logged-out homepage, we don't want to access
    // RequestInfo.kaRegion through useRegionOverride during SSR so that we have
    // a greater cache hit rate. We'll skip the query during SSR and initial
    // render and show as loading instead.
    const shouldQuery =
        (ssr && regionResult.status === "success") ||
        (!ssr &&
            renderState !== RenderState.Initial &&
            regionResult.status === "success");

    const queryOptions = !shouldQuery
        ? {skip: true}
        : {
              variables: {
                  region: regionResult.regionCode,
              },
          };

    const result = useFastlyQuery(DATA_QUERY, queryOptions);

    // @ts-expect-error - TS2322 - Type 'ReactNode' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
    return children(mapDataToResult(result));
};

export default LearnMenuTopicsQuery;

/**
 * A query hook for getting the list of domains and courses curated for the
 * request user region.
 */
export function useLearnMenuTopicsQuery(): MappedResult {
    const regionResult = useRegionOverride();

    const queryOptions =
        regionResult.status === "success"
            ? {
                  variables: {
                      region: regionResult.regionCode,
                  },
              }
            : {skip: true};

    const result = useFastlyQuery(DATA_QUERY, queryOptions);

    return mapDataToResult(result);
}

/**
 * Converts the query result to the component props
 * @param {AsyncApolloFastlyQueryResult<Data>} result The data received from the server
 *
 * @returns {MappedResult} The data ready to be consumed by the child component
 */
const mapDataToResult = (
    result: Result<learnMenuTopicsQuery>,
): MappedResult => {
    if (result.status !== "success" || result.data.learnMenuTopics == null) {
        return {
            loading: result.status === "loading" || result.status === "no-data",
            error: result.status === "error" ? result.error : undefined,
            domains: [],
        };
    }

    /**
     * SAT students need directing to the SAT mission.
     */
    const learnMenuTopics = result.data.learnMenuTopics
        .filter(isTruthy)
        .map((domain) => {
            const {
                children,
                domainId,
                isNew,
                slug,
                translatedTitle,
                href,
                icon,
            } = domain;
            return {
                domainId: domainId ?? "",
                isNew: isNew ?? false,
                slug: slug ?? "",
                translatedTitle: translatedTitle ?? "",
                href: href ?? "",
                icon: icon ?? "",
                children:
                    children?.filter(isTruthy).map((subject) => {
                        const {
                            slug,
                            courseId,
                            isNew,
                            href,
                            loggedOutHref,
                            translatedTitle,
                            nonContentLink,
                        } = subject;
                        return {
                            courseId: courseId ?? "",
                            translatedTitle: translatedTitle ?? "",
                            nonContentLink: nonContentLink ?? false,
                            slug: slug ?? "",
                            isNew: isNew ?? false,
                            href:
                                // If the user is logged-out then we show the logged-out href, if it exists.
                                loggedOutHref && !PermissionsInfo.isLoggedIn
                                    ? loggedOutHref
                                    : (href ?? ""),
                        };
                    }) || [],
            };
        });

    return {
        loading: false,
        error: undefined,
        domains: learnMenuTopics,
    };
};
